import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Truck, ShieldCheck, Flame } from "lucide-react";
import api, { LOGO_URL } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useContent } from "../context/ContentContext";
import { ProductCard } from "../components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1762406318951-cd96bf01f613";
const STORY = "https://images.unsplash.com/photo-1706684306636-e55b8b684363";
const BANNER = "https://images.unsplash.com/photo-1774989284270-df2f40b44606";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { settings, loaded } = useSettings();
  const { content: c } = useContent();
  // Only fall back to stock imagery once settings have loaded — avoids a flash
  // of placeholder photos before the owner's real branding arrives.
  const hero = settings.hero_image || (loaded ? HERO : null);
  const story = settings.brand_story_image || (loaded ? STORY : null);
  const banner = settings.monster_banner_image || (loaded ? BANNER : null);
  const logo = settings.logo_image || LOGO_URL;
  const features = [
    { icon: Truck, title: c.feature1_title, text: c.feature1_text },
    { icon: ShieldCheck, title: c.feature2_title, text: c.feature2_text },
    { icon: Flame, title: c.feature3_title, text: c.feature3_text },
  ];

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-zinc-950 min-h-[60vh] sm:min-h-[70vh] flex">
        {hero && <motion.img key={hero} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} src={hero} alt="Monster truck" className="w-full h-auto block" />}
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-[1400px] mx-auto px-6 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-block text-[#EAB308] text-xs font-bold uppercase tracking-[0.3em] mb-4 border border-[#EAB308]/40 px-3 py-1">{c.hero_eyebrow}</span>
            <h1 className="font-display text-4xl sm:text-6xl md:text-8xl uppercase leading-[0.9] tracking-tighter">
              {c.hero_title_prefix}<br /><span className="text-[#FF3B30]">{c.hero_title_highlight}</span>
            </h1>
            <p className="mt-4 sm:mt-6 text-zinc-300 text-sm sm:text-lg max-w-xl leading-relaxed hidden sm:block">
              {c.hero_subtitle}
            </p>
            <div className="mt-5 sm:mt-8 flex flex-wrap gap-3 sm:gap-4">
              <Link to="/shop" data-testid="hero-shop-btn" className="group inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-xs sm:text-sm px-5 sm:px-8 py-3 sm:py-4 hover:bg-white hover:text-black transition-colors duration-200">
                {c.hero_btn_primary} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/monster-shirts" data-testid="hero-stickers-btn" className="inline-flex items-center gap-2 border border-white/40 text-white font-bold uppercase tracking-widest text-xs sm:text-sm px-5 sm:px-8 py-3 sm:py-4 hover:bg-white hover:text-black transition-colors duration-200">
                {c.hero_btn_secondary}
              </Link>
            </div>
          </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#FF3B30] py-3 border-y border-black relative z-10">
        <Marquee speed={60} gradient={false}>
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="font-display text-2xl uppercase tracking-tight text-white mx-6">{c.marquee_text}</span>
          ))}
        </Marquee>
      </div>

      {/* FEATURES */}
      <section className="max-w-[1400px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
        {features.map((f, i) => (
          <div key={i} className="bg-zinc-950 p-8">
            <f.icon className="text-[#FF3B30] mb-4" size={28} />
            <h3 className="font-display text-xl uppercase tracking-tight">{f.title}</h3>
            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{f.text}</p>
          </div>
        ))}
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight">{c.bestsellers_prefix} <span className="text-[#FF3B30]">{c.bestsellers_highlight}</span></h2>
          <Link to="/shop" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors flex items-center gap-1">View All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* MONSTER SHIRTS HIGHLIGHT STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 mt-16">
        <Link to="/monster-shirts" data-testid="monster-shirts-strip" className="group relative block overflow-hidden border border-zinc-800 hover:border-[#FF3B30] transition-colors duration-200 bg-zinc-900">
          {banner && <motion.img key={banner} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} src={banner} alt="Monster trucks" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative z-10 px-8 py-14 sm:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <span className="inline-flex items-center gap-2 bg-[#FF3B30] text-white text-[11px] font-bold uppercase tracking-[0.3em] px-3 py-1.5 mb-4">
                <Flame size={13} /> {c.strip_tag}
              </span>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter leading-none">
                {c.strip_prefix} <span className="text-[#FF3B30]">{c.strip_highlight}</span>
              </h2>
              <p className="text-zinc-300 mt-3 max-w-lg text-sm sm:text-base">{c.strip_text}</p>
            </div>
            <span className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-sm px-8 py-4 group-hover:bg-[#FF3B30] group-hover:text-white transition-colors duration-200 whitespace-nowrap w-fit">
              {c.strip_btn} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </Link>
      </section>

      {/* BRAND STORY */}
      <section className="mt-16 border-y border-zinc-800 bg-zinc-950">
        <div className="max-w-[1400px] mx-auto px-6 py-14 sm:py-20 grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="order-2 md:order-1">
            <img src={logo} alt="MTL crest" className="h-24 w-24 sm:h-36 sm:w-36 object-contain mb-5 sm:mb-6" />
            <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tight leading-none">{c.story_prefix} <span className="text-[#FF3B30]">{c.story_highlight}</span></h2>
            <p className="text-zinc-300 text-sm sm:text-lg leading-relaxed mt-4 sm:mt-6">
              {c.story_text}
            </p>
            <Link to="/shop" className="mt-6 sm:mt-8 inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs sm:text-sm px-5 sm:px-8 py-3 sm:py-4 hover:bg-[#FF3B30] hover:text-white transition-colors duration-200">
              {c.story_btn} <ArrowRight size={18} />
            </Link>
          </div>
          <div className="order-1 md:order-2">
            {story && <motion.img key={story} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} src={story} alt="Monster Truck Lord" className="w-full max-h-[560px] object-cover border border-zinc-800" />}
          </div>
        </div>
      </section>
    </div>
  );
}
